import { processUrl } from '../actions/accountActions';
import { ProcessUrlResponse } from '../types';
import { isProtectionEnabled } from '../protectionStatus';
import {
  getYoutubeReportableUrl,
  isReportableYoutubeAPIEvent,
} from './youtubeNavigation';
import { updateSafeNetworksSettingsIfNeeded } from '../safeNetworks/safeNetworksSettings';
import { canUseSafeNetworkFeature } from '../safeNetworks';
import {
  ProcessedVerdict,
  saveVerdict,
  setPendingVerdict,
  shouldBlockByTime,
} from './verdict';
import { getDelegationVerdict } from '../delegation/delegationVerdict';
import { Verdict } from 'inter-extension-control/module/src';
import { buildBlockedBySafetyNetEvent } from '../report';
import { reportAndUpdate } from '../actions/engineActions';
import { shouldRedirectToSafeSearch } from '../safeSearch/safeSearch';
import { getChromeRuntime } from '../chrome/wrappers';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { shouldSyncScreenTime, syncScreenTime } from '../screenTime/counting';
import {
  isDelegationStarted,
  isDelegationStopped,
} from '../delegation/delegation';

const EXTENSION_INITIATOR_SCHEME = 'chrome-extension://';

export const processNetworkRequest = async (
  details: chrome.webRequest.WebRequestBodyDetails
) => {
  const reportableUrl = getReportableUrl(details);
  // Set verdict for this URL as pending
  setPendingVerdict(reportableUrl);
  // Process the request and save the new verdict
  const verdict = await process(reportableUrl, details);
  return saveVerdict(reportableUrl, verdict);
};

/**
 * Processes an HTTP event and return its final verdict based on the following conditions (order is relevant):
 *
 * Events received while the protection is disabled or delegated by Safe Networks,
 * or events that are considered noise, or not a main navigation / YouTube event,
 * it will return a non-reportable ALLOWED verdict.
 *
 * If is in delegation mode:
 *
 * And safe search was applied to the event by LW, it will return a non-reportable verdict of type IGNORE.
 *
 * And event was blocked by LW, it will report it right away, and return a verdict of type ALLOW: We don't want to block this event again.
 *
 * If it's not on delegation mode, or it is and the previous conditions were not met:
 *
 * And a time lock is active, it will return the appropriate non-reportable TIME BLOCK verdict.
 *
 * For all other cases, it will return the verdict processed by Q.
 */
const process = async (
  reportableUrl: string,
  details: chrome.webRequest.WebRequestBodyDetails
): Promise<ProcessedVerdict> => {
  // Update Safe Networks Settings periodically
  if (canUseSafeNetworkFeature()) {
    await updateSafeNetworksSettingsIfNeeded();
  }

  // Screen Time will be sync every time there's an HTTP request
  if (await shouldSyncScreenTime()) {
    await syncScreenTime();
  }

  const { requestId } = details;

  // Check if we should ignore the event
  if (shouldIgnore(details)) {
    return { verdict: ProcessUrlResponse.ALLOW, requestId };
  }

  // Check if there's any time restriction
  const timeBlockVerdict = await shouldBlockByTime();
  if (timeBlockVerdict) {
    // Return verdict if the device should be blocked by time
    return { verdict: timeBlockVerdict, requestId };
  }

  // Get delegation verdict
  let lwAppliedSafeSearch = false;
  if (isDelegationStarted()) {
    const delegationVerdict = await getDelegationVerdict(requestId);
    if (delegationVerdict?.safeSearch) {
      // LW applied safe search to this event
      lwAppliedSafeSearch = true;
    }
    if (delegationVerdict?.verdict === Verdict.Blocked) {
      // Report it as blocked by Safety Net.
      // We need to report it here because Q might not get the navigation event
      // if LW redirects to the blocker before the website is actually loaded.
      reportAndUpdate(buildBlockedBySafetyNetEvent(reportableUrl));
      // LW already blocked, so we'll treat the event as allowed
      return { verdict: ProcessUrlResponse.ALLOW, requestId };
    }
  }

  // Redirect to Safe Search if needed and LW didn't do it already
  if (shouldRedirectToSafeSearch(reportableUrl) && !lwAppliedSafeSearch) {
    return { verdict: ProcessUrlResponse.REDIRECT_TO_SAFE_SEARCH, requestId };
  }

  // Return Q verdict
  return {
    requestId,
    ...(await processUrl(reportableUrl)),
  };
};

/**
 * Returns true if protection is not enabled, delegated, it's noise, or it's not a main navigation / YouTube event.
 */
const shouldIgnore = (details: chrome.webRequest.WebRequestDetails) =>
  !isProtectionEnabled() ||
  isSafeNetworkDelegated() ||
  isDelegationStopped() ||
  isNoise(details) ||
  !(isMainFrame(details) || isReportableYoutubeAPIEvent(details.url));

/**
 * Checks if an event is reportable or not
 */
const isNoise = (details: chrome.webRequest.WebRequestDetails) =>
  isInitiatedFromAnotherExtension(details);

/**
 * Checks if the request is initiated from an extension
 */
const isInitiatedFromAnotherExtension = (
  details: chrome.webRequest.WebRequestDetails
) =>
  details.initiator?.startsWith(EXTENSION_INITIATOR_SCHEME) &&
  details.initiator !== EXTENSION_INITIATOR_SCHEME + getChromeRuntime().id;

/**
 * Returns if the request is for the main frame (document)
 */
const isMainFrame = (details: chrome.webRequest.WebRequestDetails) =>
  details.type === 'main_frame';

/**
 * Get the reportable URL for an HTTP event
 *
 * @param details - Original URL
 */
const getReportableUrl = (details: chrome.webRequest.WebRequestBodyDetails) => {
  const { url } = details;

  // Process YouTube API navigation
  // Some YouTube navigation is done through API queries instead of HTML document navigation.
  // We need this ugly cast because details can be a merge of WebRequestHeadersDetails and WebRequestBodyDetails
  if (isReportableYoutubeAPIEvent(url)) {
    const requestBody = getRequestBody(details);
    if (requestBody) {
      const reportableUrl = getYoutubeReportableUrl(requestBody);
      if (reportableUrl) {
        return reportableUrl;
      }
    }
  }

  return url;
};

/**
 * Returns the request body in a string (if any)
 *
 * @param details - Chrome's Web Request Body details
 */
const getRequestBody = (
  details: chrome.webRequest.WebRequestBodyDetails
): string | undefined => {
  const rawRequestBody = details.requestBody?.raw;
  if (rawRequestBody && rawRequestBody.length > 0) {
    // Parse requestBody bytes and join
    return rawRequestBody.reduce(
      (acc, data) =>
        data?.bytes === undefined
          ? acc
          : acc + new TextDecoder().decode(data.bytes),
      ''
    );
  }
  return undefined;
};
